import React, { ReactElement, useState } from 'react'
import { observer } from 'mobx-react'

import { PrimaryButton, SecondaryButton } from '../../components/Button'
import Checkbox from '../../components/Checkbox'
import Dropdown, { DropdownItem } from '../../components/Dropdown'
import { InputLabel, TextInputWithLabel } from '../../components/Input'
import Modal, { ModalProps } from '../../components/Modal'
import Radom, { Address, CreateExternalAccountRequest, ExternalAccountOwnerType, ExternalAccountType } from '../../state/Radom'
import { RADOM_COLORS } from '../../util/Constants'
import { ManagedPaymentMethod, getPaymentMethods } from '../../util/Managed'
import { errorToast, successToast, useTestMode } from '../../util/Util'
import { AddressForm } from '../Verify/AddressForm'
import { Countries } from '../Verify/Countries'

type Props = ModalProps & {
  customerId: string
  onAccountCreated: () => void
}

enum PayoutAccountCategory {
  Crypto = 'Crypto',
  Fiat = 'Fiat',
}

enum FiatAccountTypeOption {
  US = 'US (ACH/Wire)',
  IBAN = 'EU (IBAN)',
}

enum AccountOwnerTypeOption {
  Individual = 'Individual',
  Business = 'Business',
}

const formatAccountError = (raw: string): string => {
  const fieldMap: Record<string, string> = {
    'payee.iban.iban': 'IBAN',
    'payee.iban.bic': 'BIC',
    'payee.account.account_number': 'Account number',
    'payee.account.routing_number': 'Routing number'
  }

  const fieldMatch = raw.match(/\(field:\s*([^)]+)\)/)
  if (fieldMatch) {
    const fieldName = fieldMap[fieldMatch[1].trim()] ?? fieldMatch[1].trim()
    const message = raw.replace(/\s*\(field:.*\)/, '').trim()
    return `${message} — please check your ${fieldName}`
  }

  return raw
}

const CreateExternalAccountModal = observer(({ customerId, onAccountCreated, ...props }: Props): ReactElement => {
  const [testMode] = useTestMode()
  const [category, setCategory] = useState<PayoutAccountCategory>(PayoutAccountCategory.Crypto)

  // Crypto fields
  const [selectedCryptoType, setSelectedCryptoType] = useState<ManagedPaymentMethod>()
  const [cryptoAddress, setCryptoAddress] = useState('')
  const [cryptoAccountName, setCryptoAccountName] = useState('')

  // Common fiat fields
  const [accountOwnerType, setAccountOwnerType] = useState<AccountOwnerTypeOption>()
  const [accountOwnerName, setAccountOwnerName] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [businessName, setBusinessName] = useState('')
  const [bankName, setBankName] = useState('')
  const [fiatAccountType, setFiatAccountType] = useState<FiatAccountTypeOption>()
  const [accountOwnerAddress, setAccountOwnerAddress] = useState<Address>({} as any)

  // US fields
  const [usAccountNumber, setUsAccountNumber] = useState('')
  const [usRoutingNumber, setUsRoutingNumber] = useState('')
  const [usCheckingOrSavings, setUsCheckingOrSavings] = useState<'checking' | 'savings'>('checking')

  // IBAN fields
  const [ibanAccountNumber, setIbanAccountNumber] = useState('')
  const [ibanBic, setIbanBic] = useState('')
  const [ibanCountry, setIbanCountry] = useState('')

  const [isLoading, setIsLoading] = useState(false)

  const cryptoMethods = getPaymentMethods()
    .filter(m => !m.isFiat && !m.isDisabled && !m.isHidden && !m.hostChain)
    .filter(m => testMode ? m.isTestnet : !m.isTestnet)

  // ---- Fiat helpers ----

  const getFiatAccountType = (): ExternalAccountType => {
    switch (fiatAccountType) {
    case FiatAccountTypeOption.US: return 'us'
    case FiatAccountTypeOption.IBAN: return 'iban'
    default: return 'us'
    }
  }

  const getOwnerType = (): ExternalAccountOwnerType | undefined => {
    switch (accountOwnerType) {
    case AccountOwnerTypeOption.Individual: return 'individual'
    case AccountOwnerTypeOption.Business: return 'business'
    default: return undefined
    }
  }

  const getCurrency = (): string => {
    switch (fiatAccountType) {
    case FiatAccountTypeOption.US: return 'usd'
    case FiatAccountTypeOption.IBAN: return 'eur'
    default: return 'usd'
    }
  }

  // ---- Validation ----

  const isCryptoFormValid = (): boolean => {
    return !!selectedCryptoType && !!cryptoAddress.trim() && !!cryptoAccountName.trim()
  }

  const isFiatFormValid = (): boolean => {
    if (!fiatAccountType || !accountOwnerType || !accountOwnerName.trim()) return false
    if (!accountOwnerAddress.country || !accountOwnerAddress.city || !accountOwnerAddress.streetLine1) return false

    if (accountOwnerAddress.country === 'USA' && !accountOwnerAddress.subdivision) return false
    if (Countries.find(s => s.alpha3 === accountOwnerAddress.country)?.postal_code_format && !accountOwnerAddress.postalCode) return false

    if (accountOwnerType === AccountOwnerTypeOption.Individual && (!firstName.trim() || !lastName.trim())) return false

    if (fiatAccountType === FiatAccountTypeOption.US && (!usAccountNumber || !usRoutingNumber)) return false
    if (fiatAccountType === FiatAccountTypeOption.IBAN && (!ibanAccountNumber || !ibanBic)) return false

    return true
  }

  const isFormValid = (): boolean => {
    return category === PayoutAccountCategory.Crypto ? isCryptoFormValid() : isFiatFormValid()
  }

  // ---- Submit ----

  const createAccount = async (): Promise<void> => {
    if (!isFormValid()) return

    setIsLoading(true)
    try {
      if (category === PayoutAccountCategory.Crypto) {
        const req: CreateExternalAccountRequest = {
          customer_id: customerId,
          currency: selectedCryptoType!.ticker.toLowerCase(),
          account_type: 'crypto',
          account_owner_name: cryptoAccountName.trim(),
          crypto: {
            address: cryptoAddress.trim(),
            network: selectedCryptoType!.name
          }
        }
        await Radom.createExternalAccount(req)
      } else {
        const req: CreateExternalAccountRequest = {
          customer_id: customerId,
          currency: getCurrency(),
          account_type: getFiatAccountType(),
          account_owner_name: accountOwnerName.trim(),
          account_owner_type: getOwnerType(),
          bank_name: bankName.trim() || undefined,
          first_name: accountOwnerType === AccountOwnerTypeOption.Individual ? firstName.trim() : undefined,
          last_name: accountOwnerType === AccountOwnerTypeOption.Individual ? lastName.trim() : undefined,
          business_name: accountOwnerType === AccountOwnerTypeOption.Business ? businessName.trim() || undefined : undefined,
          address: {
            street_line_1: accountOwnerAddress.streetLine1,
            street_line_2: accountOwnerAddress.streetLine2 || undefined,
            city: accountOwnerAddress.city,
            state: accountOwnerAddress.subdivision || undefined,
            postal_code: accountOwnerAddress.postalCode || undefined,
            country: accountOwnerAddress.country
          }
        }

        if (fiatAccountType === FiatAccountTypeOption.US) {
          req.account = {
            account_number: usAccountNumber,
            routing_number: usRoutingNumber,
            checking_or_savings: usCheckingOrSavings
          }
        } else if (fiatAccountType === FiatAccountTypeOption.IBAN) {
          req.iban = {
            account_number: ibanAccountNumber,
            bic: ibanBic || undefined,
            country: ibanCountry || accountOwnerAddress.country
          }
        }

        await Radom.createExternalAccount(req)
      }

      onAccountCreated()
      successToast('Payout account created')
    } catch (err: any) {
      const raw: string = err.response?.data?.error || err.message || 'Unknown error'
      errorToast(formatAccountError(raw))
    }
    setIsLoading(false)
  }

  // ---- Render ----

  const renderCategoryPicker = (): ReactElement => (
    <div style={{ display: 'flex', gap: 20, padding: '0 0 10px 0' }}>
      <SecondaryButton
        onClick={() => setCategory(PayoutAccountCategory.Crypto)}
        type='button'
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          border: 0,
          padding: 5,
          color: category === PayoutAccountCategory.Crypto ? RADOM_COLORS.BLACK : RADOM_COLORS.GRAY_DARKEST
        }}>
        <Checkbox isRadio checked={category === PayoutAccountCategory.Crypto} />
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          <span>Crypto</span>
          <span style={{ color: RADOM_COLORS.GRAY_DARKER, fontSize: 12 }}>
            Transfer to a crypto address
          </span>
        </div>
      </SecondaryButton>

      <SecondaryButton
        onClick={() => setCategory(PayoutAccountCategory.Fiat)}
        type='button'
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          border: 0,
          padding: 5,
          color: category === PayoutAccountCategory.Fiat ? RADOM_COLORS.BLACK : RADOM_COLORS.GRAY_DARKEST
        }}>
        <Checkbox isRadio checked={category === PayoutAccountCategory.Fiat} />
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          <span>Fiat</span>
          <span style={{ color: RADOM_COLORS.GRAY_DARKER, fontSize: 12 }}>
            Offramp to a bank account
          </span>
        </div>
      </SecondaryButton>
    </div>
  )

  const renderCryptoForm = (): ReactElement => (
    <>
      <TextInputWithLabel
        label='Account name'
        placeholder='Enter a name for this account'
        disabled={isLoading}
        required
        value={cryptoAccountName}
        onChange={e => setCryptoAccountName((e.target as HTMLInputElement).value)}
        onBlur={() => setCryptoAccountName(prev => prev.trim())}
      />

      <div style={{ display: 'flex', flexGrow: 1, flexDirection: 'column', gap: 10 }}>
        <InputLabel>Network</InputLabel>
        <Dropdown
          key={selectedCryptoType?.name}
          disabled={isLoading}
          selectedContent={
            selectedCryptoType
              ? <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14 }}>
                {selectedCryptoType.logo && <img src={selectedCryptoType.logo} style={{ width: 20, height: 20 }} />}
                <span>{selectedCryptoType.currencyName ?? selectedCryptoType.name}</span>
              </div>
              : <span style={{ fontSize: 14 }}>Select network</span>
          }
          dropdownContent={
            cryptoMethods.map(m =>
              <DropdownItem
                key={m.name}
                onClick={() => setSelectedCryptoType(m)}
                style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14 }}>
                {m.logo && <img src={m.logo} style={{ width: 20, height: 20 }} />}
                <span>{m.currencyName ?? m.name}</span>
              </DropdownItem>
            )
          } />
      </div>

      <TextInputWithLabel
        label='Wallet address'
        placeholder='Enter wallet address'
        disabled={isLoading}
        required
        value={cryptoAddress}
        onChange={e => setCryptoAddress((e.target as HTMLInputElement).value)}
        onBlur={() => setCryptoAddress(prev => prev.trim())}
      />
    </>
  )

  const renderFiatForm = (): ReactElement => (
    <>
      {/* Account owner type */}
      <div style={{ display: 'flex', gap: 20 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <InputLabel>Account owner type</InputLabel>
          <Dropdown
            key={accountOwnerType}
            disabled={isLoading}
            selectedContent={
              accountOwnerType
                ? <span style={{ fontSize: 14 }}>{accountOwnerType}</span>
                : <span style={{ fontSize: 14 }}>Select owner type</span>
            }
            dropdownContent={
              [AccountOwnerTypeOption.Individual, AccountOwnerTypeOption.Business].map(c =>
                <DropdownItem
                  key={c}
                  onClick={() => setAccountOwnerType(c)}
                  style={{ fontSize: 14 }}>
                  {c}
                </DropdownItem>
              )
            } />
        </div>

        {accountOwnerType && <TextInputWithLabel
          style={{ flexGrow: 1 }}
          label={`${accountOwnerType} full name`}
          placeholder={`Enter ${accountOwnerType.toLowerCase()}'s name`}
          disabled={isLoading}
          required
          value={accountOwnerName}
          onChange={e => setAccountOwnerName((e.target as HTMLInputElement).value)}
          onBlur={() => setAccountOwnerName(prev => prev.trim())}
        />}
      </div>

      {/* Individual: first + last name */}
      {accountOwnerType === AccountOwnerTypeOption.Individual && (
        <div style={{ display: 'flex', gap: 20 }}>
          <TextInputWithLabel
            style={{ flexGrow: 1 }}
            label='First name'
            placeholder='Enter first name'
            disabled={isLoading}
            required
            value={firstName}
            onChange={e => setFirstName((e.target as HTMLInputElement).value)}
            onBlur={() => setFirstName(prev => prev.trim())}
          />
          <TextInputWithLabel
            style={{ flexGrow: 1 }}
            label='Last name'
            placeholder='Enter last name'
            disabled={isLoading}
            required
            value={lastName}
            onChange={e => setLastName((e.target as HTMLInputElement).value)}
            onBlur={() => setLastName(prev => prev.trim())}
          />
        </div>
      )}

      {/* Business name */}
      {accountOwnerType === AccountOwnerTypeOption.Business && (
        <TextInputWithLabel
          label='Business name'
          placeholder='Enter business name'
          disabled={isLoading}
          value={businessName}
          onChange={e => setBusinessName((e.target as HTMLInputElement).value)}
          onBlur={() => setBusinessName(prev => prev.trim())}
        />
      )}

      {/* Account type */}
      <div style={{ display: 'flex', flexGrow: 1, flexDirection: 'column', gap: 10 }}>
        <InputLabel>Account type</InputLabel>
        <Dropdown
          key={fiatAccountType}
          disabled={isLoading}
          selectedContent={
            fiatAccountType
              ? <span style={{ fontSize: 14 }}>{fiatAccountType}</span>
              : <span style={{ fontSize: 14 }}>Select bank account type</span>
          }
          dropdownContent={
            [FiatAccountTypeOption.IBAN, FiatAccountTypeOption.US].map(c =>
              <DropdownItem
                key={c}
                onClick={() => setFiatAccountType(c)}
                style={{ fontSize: 14 }}>
                {c}
              </DropdownItem>
            )
          } />
      </div>

      {/* Bank name (optional) */}
      {fiatAccountType && (
        <TextInputWithLabel
          label='Bank name (optional)'
          placeholder='e.g. Chase, Wells Fargo'
          disabled={isLoading}
          value={bankName}
          onChange={e => setBankName((e.target as HTMLInputElement).value)}
          onBlur={() => setBankName(prev => prev.trim())}
        />
      )}

      {/* US fields */}
      {fiatAccountType === FiatAccountTypeOption.US && <>
        <TextInputWithLabel
          label='Account number'
          placeholder='Enter account number'
          disabled={isLoading}
          required
          value={usAccountNumber}
          onChange={e => setUsAccountNumber((e.target as HTMLInputElement).value)}
          onBlur={() => setUsAccountNumber(prev => prev.replace(/\D+/g, ''))}
        />
        <TextInputWithLabel
          label='Routing number'
          placeholder='Enter routing number'
          disabled={isLoading}
          required
          value={usRoutingNumber}
          onChange={e => setUsRoutingNumber((e.target as HTMLInputElement).value)}
          onBlur={() => setUsRoutingNumber(prev => prev.replace(/\D+/g, '').slice(0, 9))}
        />
        <div style={{ display: 'flex', flexGrow: 1, flexDirection: 'column', gap: 10 }}>
          <InputLabel>Checking or Savings</InputLabel>
          <Dropdown
            disabled={isLoading}
            selectedContent={<span style={{ fontSize: 14 }}>{usCheckingOrSavings === 'checking' ? 'Checking' : 'Savings'}</span>}
            dropdownContent={
              (['checking', 'savings'] as const).map(c =>
                <DropdownItem
                  key={c}
                  onClick={() => setUsCheckingOrSavings(c)}
                  style={{ fontSize: 14 }}>
                  {c === 'checking' ? 'Checking' : 'Savings'}
                </DropdownItem>
              )
            } />
        </div>
      </>}

      {/* IBAN fields */}
      {fiatAccountType === FiatAccountTypeOption.IBAN && <>
        <TextInputWithLabel
          label='IBAN'
          placeholder='Enter IBAN'
          disabled={isLoading}
          required
          value={ibanAccountNumber}
          onChange={e => setIbanAccountNumber((e.target as HTMLInputElement).value)}
          onBlur={() => setIbanAccountNumber(prev => prev.replace(/\s+/g, '').toUpperCase())}
        />
        <TextInputWithLabel
          label='BIC'
          placeholder='Enter BIC'
          disabled={isLoading}
          required
          value={ibanBic}
          onChange={e => setIbanBic((e.target as HTMLInputElement).value)}
          onBlur={() => setIbanBic(prev => prev.replace(/\s+/g, '').toUpperCase())}
        />
      </>}

      {/* Address form */}
      {fiatAccountType && <AddressForm state={accountOwnerAddress} onUpdate={(s) => setAccountOwnerAddress(s)} />}
    </>
  )

  return <Modal {...props} innerStyles={{ width: 550 }}>
    <div style={{
      display: 'flex',
      flexGrow: 1,
      flexDirection: 'column',
      justifyContent: 'start',
      gap: 10,
      padding: '0px 30px'
    }}>
      <div style={{ display: 'flex', flexGrow: 1, flexDirection: 'column', rowGap: 20 }}>
        {renderCategoryPicker()}
        {category === PayoutAccountCategory.Crypto ? renderCryptoForm() : renderFiatForm()}
      </div>
    </div>

    <div style={{
      padding: 30,
      borderRadius: 15,
      borderTopLeftRadius: 0,
      borderTopRightRadius: 0,
      position: 'sticky',
      bottom: 0,
      background: 'linear-gradient(transparent, white 20%)'
    }}>
      <PrimaryButton
        disabled={!isFormValid()}
        isLoading={isLoading}
        style={{ width: '100%' }}
        onClick={createAccount}>
        Create account
      </PrimaryButton>
    </div>
  </Modal>
})

export default CreateExternalAccountModal
